import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Download, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { submitForm } from "@/lib/api";
import { trackLead } from "@/lib/pixel";
import { CTAButton } from "@/components/primitives";

const field = "w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm text-navy outline-none transition-colors focus:border-emerald2";

export const DOCS = {
  brochure: { url: "/assets/docs/bharat-ethanol-forum-brochure-2026.pdf", name: "Bharat-Ethanol-Forum-2026-Brochure.pdf" },
  sponsorProposal: { url: "/assets/docs/bharat-ethanol-forum-sponsorship-proposal-2026.pdf", name: "Bharat-Ethanol-Forum-2026-Sponsorship-Proposal.pdf" },
};

export const INTEREST_OPTIONS = [
  "Delegate",
  "Exhibitor",
  "Sponsor",
  "Speaker",
  "General Enquiry",
];

function triggerDownload(url, name) {
  const a = document.createElement("a");
  a.href = url;
  if (name) a.download = name;
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export default function LeadDialog({
  trigger,
  formType,
  title,
  description,
  ctaLabel = "Submit",
  successMsg = "Thanks — we'll be in touch.",
  extraFields = [],
  downloadUrl,
  downloadName,
  onSuccess,
  onClose,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", organisation: "", interest: "" });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => {
    setForm({ name: "", email: "", phone: "", organisation: "", interest: "" });
    setDone(false);
  };

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { name, email, phone, organisation, interest, ...rest } = form;
      const res = await submitForm({ form_type: formType, name, email, phone, organisation, interest, extra: rest });
      const [firstName, ...lastParts] = String(name).trim().split(/\s+/);
      trackLead(formType, res?.id, { email, phone, first_name: firstName, last_name: lastParts.join(" ") });
      toast.success(successMsg);
      onSuccess?.(res);
      if (downloadUrl) {
        triggerDownload(downloadUrl, downloadName);
        setDone(true);
      } else {
        setOpen(false);
        reset();
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (!v) {
      reset();
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid={`lead-dialog-${formType}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-navy">{title}</DialogTitle>
          {description && <DialogDescription className="text-navy/60">{description}</DialogDescription>}
        </DialogHeader>

        {done ? (
          <div className="mt-2 flex flex-col items-center py-4 text-center" data-testid="lead-success">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald2/10 text-emerald2">
              <CheckCircle2 className="h-7 w-7" />
            </span>
            <h4 className="mt-4 font-display text-xl font-bold text-navy">Your download is ready</h4>
            <p className="mt-2 text-sm text-navy/60">
              The file should open automatically. If it didn't, tap the button below.
            </p>
            <CTAButton
              href={downloadUrl}
              variant="primary"
              className="mt-5 w-full"
              data-testid="lead-download-again"
              onClick={() => triggerDownload(downloadUrl, downloadName)}
            >
              <Download className="h-4 w-4" /> Download again
            </CTAButton>
          </div>
        ) : (
          <form onSubmit={handle} className="mt-2 space-y-3">
            <input data-testid="lead-name" className={field} placeholder="Full name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
            <input data-testid="lead-email" type="email" className={field} placeholder="Work email" required value={form.email} onChange={(e) => set("email", e.target.value)} />
            <input data-testid="lead-phone" type="tel" className={field} placeholder="Phone (with country code)" required value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            <input data-testid="lead-organisation" className={field} placeholder="Company / Organisation" required value={form.organisation} onChange={(e) => set("organisation", e.target.value)} />
            <select data-testid="lead-interest" className={`${field} appearance-none`} required value={form.interest} onChange={(e) => set("interest", e.target.value)}>
              <option value="" disabled>I'm interested as a…</option>
              {INTEREST_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            {extraFields.map((f) => (
              <input key={f.key} data-testid={`lead-${f.key}`} className={field} placeholder={f.placeholder} value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
            ))}
            <CTAButton type="submit" data-testid="lead-submit" className="w-full" variant="primary" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : downloadUrl ? <Download className="h-4 w-4" /> : null}
              {ctaLabel}
            </CTAButton>
            <p className="text-center text-xs text-navy/50">No spam. We'll send the resource and occasional forum updates.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
